import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/api";
import { Loader2, ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

/**
 * Paginates through DICOM slices extracted from a completed ZIP upload.
 * Server renders each slice to PNG on demand (cached 1h by the browser).
 *
 * Props:
 *   uploadRowId  — id of a completed R2 ZIP upload
 *   authToken    — JWT (passed as query arg for <img> tag auth)
 */
export default function DicomSliceBrowser({ uploadRowId, authToken }) {
  const [index, setIndex] = useState(0);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/dicom-uploads/${uploadRowId}/slices`)
      .then((r) => { if (alive) setMeta(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Could not load slices"); });
    return () => { alive = false; };
  }, [uploadRowId]);

  const imgUrl = useMemo(() => {
    if (!authToken || !meta || meta.count <= 0) return null;
    return `${API}/admin/dicom-uploads/${uploadRowId}/slice/${index}.png?auth=${encodeURIComponent(authToken)}`;
  }, [uploadRowId, index, meta, authToken]);

  // Keyboard nav for radiologists (← / →)
  useEffect(() => {
    const onKey = (e) => {
      if (!meta || meta.count <= 0) return;
      if (e.key === "ArrowLeft")  setIndex((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIndex((i) => Math.min(meta.count - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [meta]);

  if (err) return (
    <Card className="p-4 bg-rose-50 border-rose-200 text-sm text-rose-700" data-testid="slice-browser-error">{err}</Card>
  );
  if (!meta) return (
    <Card className="p-6 grid place-items-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2"/> Loading slice index…</Card>
  );
  if (meta.extract_status === "running") return (
    <Card className="p-6 text-sm text-slate-600 text-center" data-testid="slice-browser-extracting">
      <Loader2 className="w-4 h-4 animate-spin inline-block mr-2 text-indigo-600"/>
      Extracting DICOM slices from the ZIP — this usually finishes within a minute. Refresh to check.
    </Card>
  );
  if (!meta.count) return (
    <Card className="p-6 text-sm text-slate-500 text-center" data-testid="slice-browser-empty">
      No DICOM slices found inside {meta.filename}. The archive may contain only screenshots or non-DICOM files.
    </Card>
  );

  return (
    <Card className="p-4 border-slate-200" data-testid="slice-browser">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-indigo-600"/>
          <div className="font-heading font-semibold text-slate-900 text-sm truncate">{meta.filename}</div>
        </div>
        <div className="text-xs text-slate-500 tabular-nums">Slice {index + 1} / {meta.count}</div>
      </div>
      <div className="relative bg-black rounded-lg overflow-hidden aspect-square max-h-[600px] flex items-center justify-center">
        {imgLoading && <Loader2 className="w-6 h-6 text-white animate-spin absolute"/>}
        {imgUrl && (
          <img
            key={imgUrl}
            src={imgUrl}
            alt={`Slice ${index + 1}`}
            onLoad={() => setImgLoading(false)}
            onLoadStart={() => setImgLoading(true)}
            className="max-h-[600px] w-auto"
            data-testid="slice-browser-image"
          />
        )}
      </div>
      <input
        type="range" min={0} max={meta.count - 1} step={1} value={index}
        onChange={(e) => setIndex(Number(e.target.value))}
        className="w-full mt-3 accent-indigo-600"
        data-testid="slice-browser-scrubber"
      />
      <div className="mt-2 flex items-center justify-between">
        <Button size="sm" variant="outline" onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={index === 0} data-testid="slice-prev">
          <ChevronLeft className="w-4 h-4"/> Prev
        </Button>
        <div className="text-[10px] text-slate-500">← → keys to scrub</div>
        <Button size="sm" variant="outline" onClick={() => setIndex((i) => Math.min(meta.count - 1, i + 1))} disabled={index === meta.count - 1} data-testid="slice-next">
          Next <ChevronRight className="w-4 h-4"/>
        </Button>
      </div>
    </Card>
  );
}
